'use client';

import React, { useEffect, useRef } from 'react';

const stats = [
  { num: '10,000+', label: 'Footfall across 6 days', icon: '👥' },
  { num: '2,763', label: 'Seats at Gala Evening', icon: '🏛️' },
  { num: '6 Days', label: 'Sponsor visibility window', icon: '📅' },
  { num: '450+', label: 'Members in 2025 (organic)', icon: '🎫' },
  { num: '3', label: 'National media partners', icon: '📺' },
  { num: '50,000+', label: 'Digital reach (@HOYICHOYI)', icon: '📱' },
];

const audiencePoints = [
  'Affluent South-Central Mumbai families - L&T Crescent Bay, Ashok Towers, Island City Centre, Celestia Spaces',
  'Cross-cultural audience - Bengali, Marathi, Tamil, Marwari, and Gujarati families',
  'Drive-in audience from Virar, Navi Mumbai, Palghar for the Gala Evening',
  'Families with children - HCAF is a children-first, inclusive platform',
  'Paid Meta digital campaign running Aug–Oct 2026 across @HOYICHOYI',
];

export default function WhySponsorSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => { entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add('active'); }); },
      { threshold: 0.08 }
    );
    sectionRef?.current?.querySelectorAll('.reveal')?.forEach((el) => observer?.observe(el));
    return () => observer?.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="bg-background alpona-bg w-full px-4 md:px-12 pt-14 md:pt-20 pb-12 md:pb-16">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 md:gap-12 items-start">
          {/* Left: Stats */}
          <div className="lg:col-span-7">
            <div className="mb-8 md:mb-10 reveal">
              <span className="inline-block px-4 py-1.5 rounded-full border border-border text-muted-foreground text-xs font-bold tracking-widest uppercase mb-4 md:mb-5">
                Why Sponsor
              </span>
              <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-black text-foreground tracking-tight mb-3 md:mb-4">
                Proven Demand. <span className="text-primary">Premium Audience.</span>
              </h2>
              <p className="text-muted-foreground text-sm md:text-base leading-relaxed">
                450 members, over 10,000 footfall over 5 days of Puja - all before a single rupee spent on digital advertising. Real product-market fit, not a cold-start event.
              </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
              {stats?.map((s, i) => (
                <div
                  key={s?.label}
                  className="reveal bg-card border border-border rounded-2xl md:rounded-3xl p-4 md:p-5 hover:shadow-gold hover:-translate-y-0.5 transition-all duration-300"
                  style={{ transitionDelay: `${i * 80}ms` }}
                >
                  <span className="text-xl md:text-2xl block mb-2 md:mb-3">{s?.icon}</span>
                  <p className="font-display text-xl md:text-2xl font-black text-primary mb-1">{s?.num}</p>
                  <p className="text-muted-foreground text-xs leading-snug">{s?.label}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Right: Audience */}
          <div className="lg:col-span-5 reveal reveal-delay-200">
            <div className="bg-dark-bg border border-accent/15 rounded-3xl md:rounded-4xl p-6 md:p-8 h-full">
              <h3 className="font-display text-lg md:text-xl font-black text-white tracking-tight mb-5 md:mb-6">
                A Premium, Captive Audience
              </h3>
              <ul className="space-y-3 md:space-y-4">
                {audiencePoints?.map((pt) => (
                  <li key={pt} className="flex items-start gap-3 text-sm text-white/70">
                    <svg className="text-accent flex-shrink-0 mt-0.5" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M20 6 9 17l-5-5"/>
                    </svg>
                    {pt}
                  </li>
                ))}
              </ul>
              <div className="mt-6 md:mt-8 pt-5 md:pt-6 border-t border-white/10">
                <p className="text-accent text-xs font-bold tracking-widest uppercase mb-3">2025 Media Partners</p>
                <div className="flex flex-wrap gap-2">
                  {['India Today', 'Aaj Tak', 'TV9 Bharatvarsh']?.map((m) => (
                    <span key={m} className="px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-white/60 text-xs font-semibold">{m}</span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}